import { IStore } from 'redux/IStore';
import { IPage } from 'models/common';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
import { IPollsData, IPollsAction } from 'models/polls/polls';
import { IPoll } from 'models/polls/poll';
const { notifSend } = notifActions;

/** Action Types */
export const REQUEST_START: string = 'polls/REQUEST_START';
export const REQUEST_FAILURE: string = 'polls/REQUEST_FAILURE';
export const GET_POLLS_SUCCESS: string = 'polls/GET_POLLS_SUCCESS';
export const SET_ITEMS_PAGE: string = 'polls/SET_ITEMS_PAGE';
export const RESET_DATA: string = 'polls/RESET_DATA';
export const SET_ISFIRSTLOAD: string = 'polls/SET_ISFIRSTLOAD';
export const SET_STATUS_FILTER: string = 'polls/SET_STATUS_FILTER';
export const SET_SEARCH_FILTER: string = 'polls/SET_SEARCH_FILTER';
export const SET_SORT_FILTER: string = 'polls/SET_SORT_FILTER';
export const RESET_FILTERS: string = 'polls/RESET_FILTERS';

/** Initial State */
const initialState: IPollsData = {
	items: [],
	isFetching: false,
	itemsPage: {
		currentPage: 1,
		totalPages: 0,
		totalItems: 0,
		itemsPerPage: 10,
		items: [],
	},
	isFirstLoad: true,
	statusFilter: null,
	sortFilter: null,
	keywordFilter: '',
};

/** Reducer */
export function pollsReducer(state = initialState, action: IPollsAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case GET_POLLS_SUCCESS:
			return Object.assign({}, state, {
				items: action.payload.items,
				isFetching: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case SET_ITEMS_PAGE:
			return Object.assign({}, state, {
				itemsPage: action.payload.itemsPage,
			});

		case RESET_DATA:
			return Object.assign({}, state, {
				items: [],
				itemsPage: {
					currentPage: 0,
					totalPages: 0,
					totalItems: 0,
					itemsPerPage: 10,
					items: [],
				},
				userId: null,
			});

		case RESET_FILTERS:
			return Object.assign({}, state, {
				keywordFilter: '',
				sortFilter: null,
				statusFilter: null,
			});

		case SET_ISFIRSTLOAD:
			return Object.assign({}, state, {
				isFirstLoad: action.payload.isFirstLoad,
			});

		case SET_SEARCH_FILTER:
			return Object.assign({}, state, {
				keywordFilter: action.payload.keywordFilter,
				isFirstLoad: true,
			});

		case SET_STATUS_FILTER:
			return Object.assign({}, state, {
				statusFilter: action.payload.statusFilter,
				isFirstLoad: true,
			});

		case SET_SORT_FILTER:
			return Object.assign({}, state, {
				sortFilter: action.payload.sortFilter,
				isFirstLoad: true,
			});

		default:
			return state;
	}
}

/** Action Creator */
export function getPolls() {
	return (dispatch, getState: () => IStore) => {
		if (getState().polls.isFirstLoad) {
			dispatch(resetPollsData());
			dispatch(setPollsIsFirstLoad(false));
		}

		dispatch(requestStart());

		const currentPage: number = getState().polls.itemsPage.currentPage;
		const newPage: number = currentPage + 1;
		const itemsPerPage: number = getState().polls.itemsPage.itemsPerPage;

		let queryString = '?';
		queryString = queryString + '&currentPage=' + newPage;
		queryString = queryString + '&itemsPerPage=' + itemsPerPage;

		if (getState().polls.keywordFilter && getState().polls.keywordFilter !== '') {
			queryString = queryString + '&keyword=' + getState().polls.keywordFilter;
		}

		if (getState().polls.statusFilter !== null) {
			queryString = queryString + '&pollStatus=' + getState().polls.statusFilter;
		}

		if (getState().polls.sortFilter !== null) {
			queryString = queryString + '&sortColumn=' + getState().polls.sortFilter;
		}

		return fetch(
			getState().fetchDefault.baseAPI + 'api/Poll/All/' + queryString,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'GET',
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => {
						const pollsState: IPollsData = getState().polls;
						const items: IPoll[] = pollsState.items;
						const newItems: IPoll[] = res.items;
						Array.prototype.push.apply(items, newItems);
						dispatch(getPollsSuccess(items));
						dispatch(setPollItemsPage(res));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function deletePoll(id: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());
		return fetch(
			getState().fetchDefault.baseAPI + 'api/Poll/' + id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'DELETE',
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(deletePollFromList(id));
					dispatch(
						notifSend({ message: i18n.t('Notification_Success_Poll_Deleted'), kind: 'success', dismissAfter: 3000 }),
					);
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

export function addPollInList(poll: IPoll) {
	return (dispatch, getState: () => IStore) => {
		const items = getState().polls.items;
		dispatch(getPollsSuccess(items.concat(poll)));
	};
}

/** Action Creator */
export function updatePollInList(poll: IPoll) {
	return (dispatch, getState: () => IStore) => {
		const { items } = getState().polls;

		const itemInList = items.filter((x) => x.id === poll.id)[0];
		if (itemInList) {
			const itemIndex = items.indexOf(itemInList);
			items[itemIndex] = poll;

			dispatch(getPollsSuccess(items));
		}
	};
}

/** Action Creator */
export function deletePollFromList(id: string) {
	return (dispatch, getState: () => IStore) => {
		const items: IPoll[] = getState().polls.items;
		const index = items.indexOf(items.find((x: IPoll) => x.id === id));
		items.splice(index, 1);
		dispatch(getPollsSuccess(items));
	};
}

/** Action Creator */
export function requestStart(): IPollsAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function getPollsSuccess(items: IPoll[]): IPollsAction {
	return {
		type: GET_POLLS_SUCCESS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function requestFailure(message: any): IPollsAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function setPollItemsPage(itemsPage: IPage): IPollsAction {
	return {
		type: SET_ITEMS_PAGE,
		payload: {
			itemsPage,
		},
	};
}

/** Action Creator */
export function resetPollsData(): IPollsAction {
	return {
		type: RESET_DATA,
	};
}

export function setPollsIsFirstLoad(isFirstLoad: boolean): IPollsAction {
	return {
		type: SET_ISFIRSTLOAD,
		payload: {
			isFirstLoad,
		},
	};
}

export function setPollsStatusFilter(statusFilter: string): IPollsAction {
	return {
		type: SET_STATUS_FILTER,
		payload: {
			statusFilter,
		},
	};
}

export function setPollsSearchFilter(keywordFilter: string): IPollsAction {
	return {
		type: SET_SEARCH_FILTER,
		payload: {
			keywordFilter,
		},
	};
}

export function setPollsSortFilter(sortFilter: string): IPollsAction {
	return {
		type: SET_SORT_FILTER,
		payload: {
			sortFilter,
		},
	};
}

export function resetPollsFilters(): IPollsAction {
	return {
		type: RESET_FILTERS,
	};
}
